import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Receipt, ArrowRight, CheckCircle, AlertTriangle, FileText, DollarSign } from 'lucide-react';

const FinalValidation = ({ uploadedFiles = [] }) => {
  const [showMovement, setShowMovement] = useState(false);

  const taxData = [
    {
      id: 'industria',
      name: 'Industria y comercio',
      amount: 16357,
      description: 'Impuesto municipal',
      type: 'Municipal',
      icon: Receipt
    },
    {
      id: 'iva',
      name: 'IVA',
      amount: 2258,
      description: 'Impuesto al Valor Agregado',
      type: 'Nacional',
      icon: FileText
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('COP', '$');
  };

  const totalTaxes = taxData.reduce((sum, tax) => sum + tax.amount, 0);

  const startMovement = () => {
    setShowMovement(true);
  };

  return (
    <div style={{ padding: '24px', width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', padding: '12px', borderRadius: '12px' }}>
          <Receipt size={24} />
        </div>
        <div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
            Anticipos de impuestos detectados
          </h2>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '16px' }}>
            Activos no financieros (Nota 6) - Reclasificación de anticipos
          </p>
        </div>
      </div>

      {/* Tax Values List */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
        {taxData.map((tax, index) => {
          const Icon = tax.icon;
          
          return (
            <motion.div
              key={tax.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              style={{
                background: 'white',
                border: '2px solid #f59e0b',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 4px 16px rgba(245, 158, 11, 0.2)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Background Pattern */}
              <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '100px',
                height: '100px',
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.05))',
                borderRadius: '50%'
              }} />

              {/* Content */}
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{
                    background: '#f59e0b',
                    color: 'white',
                    padding: '10px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                      {tax.name}
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                      {tax.description}
                    </p>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b', marginBottom: '4px' }}>
                    {formatCurrency(tax.amount)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#92400e' }}>
                    Tipo: {tax.type}
                  </div>
                </div>

                {/* Status Badge */}
                <div style={{
                  background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertTriangle size={14} style={{ color: '#d97706' }} />
                  <span style={{ fontSize: '12px', color: '#92400e', fontWeight: '600' }}>
                    REQUIERE RECLASIFICACIÓN
                  </span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          border: '2px solid #e2e8f0',
          borderRadius: '16px',
          padding: '24px',
          marginTop: '16px'
        }}
      >
        <h3 style={{ 
          margin: '0 0 16px 0', 
          fontSize: '18px', 
          fontWeight: '600', 
          color: '#1f2937',
          textAlign: 'center'
        }}>
          Resumen de Anticipos Detectados
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '20px' }}>
          <div style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <DollarSign size={24} style={{ color: '#f59e0b', marginBottom: '8px' }} />
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b', marginBottom: '4px' }}>
              {formatCurrency(totalTaxes)}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Total Anticipos
            </div>
          </div>

          <div style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <Receipt size={24} style={{ color: '#3b82f6', marginBottom: '8px' }} />
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#3b82f6', marginBottom: '4px' }}>
              2
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Tipos de Impuesto
            </div>
          </div>

          <div style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <AlertTriangle size={24} style={{ color: '#ef4444', marginBottom: '8px' }} />
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#ef4444', marginBottom: '4px' }}>
              Pendiente
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Estado
            </div>
          </div>
        </div>

        {/* Start Movement Button */}
        {!showMovement && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={startMovement}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              padding: '14px 24px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)'
            }}
          >
            <ArrowRight size={20} />
            Ejecutar Reclasificación
          </motion.button>
        )}
      </motion.div>

      {/* Movement Animation */}
      <AnimatePresence>
        {showMovement && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={{
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              border: '2px solid #3b82f6',
              borderRadius: '16px',
              padding: '32px',
              marginTop: '24px',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Background decoration */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%)',
              transform: 'rotate(45deg)'
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <h3 style={{ 
                margin: '0 0 24px 0', 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#1e40af',
                textAlign: 'center'
              }}>
                Movimiento: Activos no financieros → Activo circulante / Anticipo de impuestos
              </h3>

              {/* Movement Flow */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'center', marginBottom: '24px' }}>
                
                {/* From */}
                <motion.div
                  initial={{ scale: 1 }}
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{
                    background: 'white',
                    border: '2px solid #ef4444',
                    borderRadius: '12px',
                    padding: '20px',
                    minWidth: '220px',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ fontSize: '14px', color: '#dc2626', fontWeight: '600', marginBottom: '8px' }}>
                    DESDE: Activos no financieros
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#dc2626', marginBottom: '8px' }}>
                    {formatCurrency(totalTaxes)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#ef4444' }}>
                    Anticipos de impuestos
                  </div>
                </motion.div>

                {/* Arrow */}
                <motion.div
                  animate={{ x: [0, 15, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  style={{ color: '#3b82f6' }}
                >
                  <ArrowRight size={40} />
                </motion.div>

                {/* To */}
                <motion.div
                  initial={{ scale: 1 }}
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                  style={{
                    background: 'white',
                    border: '2px solid #10b981',
                    borderRadius: '12px',
                    padding: '20px',
                    minWidth: '220px',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ fontSize: '14px', color: '#059669', fontWeight: '600', marginBottom: '8px' }}>
                    HACIA: Activo circulante
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#059669', marginBottom: '8px' }}>
                    {formatCurrency(totalTaxes)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#10b981' }}>
                    Anticipo de impuestos
                  </div>
                </motion.div>
              </div>

              {/* Individual Items Movement */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                {taxData.map((tax, index) => (
                  <motion.div
                    key={tax.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 + index * 0.2 }}
                    style={{
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '12px',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                      {tax.name}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>
                      {formatCurrency(tax.amount)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                      ✓ Reclasificado
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Success Message */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.2 }}
                style={{
                  background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                  border: '2px solid #10b981',
                  borderRadius: '12px',
                  padding: '20px',
                  textAlign: 'center'
                }}
              >
                <CheckCircle size={32} style={{ color: '#10b981', marginBottom: '12px' }} />
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#065f46', marginBottom: '8px' }}>
                  Reclasificación Completada
                </div>
                <div style={{ fontSize: '14px', color: '#047857', lineHeight: '1.5' }}>
                  Los anticipos de impuestos han sido correctamente reclasificados desde "Activos no financieros" 
                  hacia "Activo circulante / Anticipo de impuestos" por un total de {formatCurrency(totalTaxes)}.
                </div>
                
                {/* Details */}
                <div style={{ 
                  marginTop: '16px', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '24px',
                  fontSize: '12px',
                  color: '#047857'
                }}>
                  <div>• Industria y comercio: {formatCurrency(taxData[0].amount)}</div>
                  <div>• IVA: {formatCurrency(taxData[1].amount)}</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FinalValidation; 